import type { ReactNode } from "react";
import { Button } from "react-bootstrap";
import { FiArrowRight } from "react-icons/fi";
import { ClampedTextWithTooltip } from "./events/ClampedTextWithTooltip";
import "./css/DashboardCards.css";

export type DashboardCardVariant = "default" | "flagship" | "soon";
export type DashboardCardStatusTone =
  | "live"
  | "new"
  | "pending"
  | "soon"
  | "flagship";

export type DashboardCardStat = {
  value: string;
  label: string;
};

export type DashboardCardProps = {
  variant?: DashboardCardVariant;
  icon: ReactNode;
  status: { label: string; tone: DashboardCardStatusTone };
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  ctaDisabled?: boolean;
  stats?: DashboardCardStat[];
};

const statusClass: Record<DashboardCardStatusTone, string> = {
  live: "dashboard-card__pill--live",
  new: "dashboard-card__pill--new",
  pending: "dashboard-card__pill--pending",
  soon: "dashboard-card__pill--soon",
  flagship: "dashboard-card__pill--flagship",
};

export function DashboardCard({
  variant = "default",
  icon,
  status,
  title,
  description,
  ctaLabel,
  ctaHref,
  onCtaClick,
  ctaDisabled = false,
  stats,
}: DashboardCardProps) {
  const isSoon = variant === "soon";
  const isFlagship = variant === "flagship";
  const interactive = !isSoon && !ctaDisabled;

  const handleCta = () => {
    if (!interactive) return;
    onCtaClick?.();
  };

  /** Match smbf.global: primary = brand gradient; outline = navy ghost; soon = disabled. */
  const ctaClass = [
    "dashboard-card__cta",
    "w-100",
    "d-inline-flex",
    "align-items-center",
    "justify-content-center",
    "gap-2",
    "fw-semibold",
    isSoon || ctaDisabled
      ? "btn btn-outline-secondary"
      : isFlagship
        ? "btn btn-primary"
        : "btn btn-outline-primary btn-hover-project-gradient",
  ].join(" ");

  const ctaContent = (
    <>
      <span>{ctaLabel}</span>
      {isFlagship && interactive ? (
        <FiArrowRight size={16} aria-hidden />
      ) : null}
    </>
  );

  return (
    <article
      className={[
        "dashboard-card",
        "d-flex",
        "flex-column",
        "h-100",
        isFlagship ? "dashboard-card--flagship" : "",
        isSoon ? "dashboard-card--soon" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="dashboard-card__accent" aria-hidden />

      <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <div
          className={[
            "dashboard-card__icon",
            "flex-shrink-0",
            isFlagship ? "dashboard-card__icon--flagship" : "",
            isSoon ? "dashboard-card__icon--soon" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {icon}
        </div>
        <span
          className={[
            "dashboard-card__pill",
            "badge",
            "rounded-pill",
            "fw-semibold",
            "text-uppercase",
            statusClass[status.tone],
          ].join(" ")}
        >
          {status.label}
        </span>
      </div>

      <h3
        className={[
          "dashboard-card__title",
          "fw-bold",
          "mb-2",
          isSoon ? "dashboard-card__title--soon" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {title}
      </h3>

      <ClampedTextWithTooltip
        id={`dashboard-card-desc-${title}`}
        text={description}
        className="dashboard-card__desc mb-0 flex-grow-1"
        lines={3}
      />

      {isFlagship && stats && stats.length > 0 ? (
        <div className="dashboard-card__stats d-flex gap-4 mt-3 pt-3">
          {stats.map((stat) => (
            <div key={`${stat.label}-${stat.value}`}>
              <div className="dashboard-card__stat-value fw-bold">
                {stat.value}
              </div>
              <div className="dashboard-card__stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-auto pt-4">
        <Button
          type="button"
          className={ctaClass}
          disabled={!interactive}
          aria-disabled={!interactive}
          href={ctaHref && interactive ? ctaHref : undefined}
          onClick={(e) => {
            if (ctaHref && interactive) {
              e.preventDefault();
            }
            handleCta();
          }}
        >
          {ctaContent}
        </Button>
      </div>
    </article>
  );
}

export default DashboardCard;
